import React from 'react';
import { Calculator, DollarSign, Percent, PlusCircle, MinusCircle, AlertCircle } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета НДС
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  const isAddCalculation = results.calculationType === 'add';
  const vatRateDisplay = results.vatRate === 0 ? '0%' : `${formatNumber(results.vatRate, 2)}%`;

  return (
    <ToolResult title="Результаты расчета НДС">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
        {/* Сумма без НДС */}
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm">
          <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
            <DollarSign className="w-4 h-4"/>
            Сумма без НДС
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-blue-800 dark:text-blue-200" aria-live="polite">
              {formatCurrency(results.amountWithoutVAT, results.precision)}
            </span>
          </div>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            {isAddCalculation ? 'Исходная сумма' : 'Вычисленная сумма без налога'}
          </p>
        </div>

        {/* Сумма НДС */}
        <div className="rounded-lg bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 p-5 border border-green-100 dark:border-green-800/50 shadow-sm">
          <h3 className="text-base font-medium text-green-700 dark:text-green-300 mb-1.5 flex items-center gap-1.5">
            <Percent className="w-4 h-4"/>
            Сумма НДС
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-green-800 dark:text-green-200" aria-live="polite">
              {formatCurrency(results.vatAmount, results.precision)}
            </span>
          </div>
          <p className="text-xs text-green-600 dark:text-green-400 mt-1">
            {vatRateDisplay} от {isAddCalculation ? 'исходной суммы' : 'суммы с НДС'}
          </p>
        </div>

        {/* Сумма с НДС */}
        <div className="rounded-lg bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/30 dark:to-violet-900/30 p-5 border border-purple-100 dark:border-purple-800/50 shadow-sm">
          <h3 className="text-base font-medium text-purple-700 dark:text-purple-300 mb-1.5 flex items-center gap-1.5">
            <DollarSign className="w-4 h-4"/>
            Сумма с НДС
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-purple-800 dark:text-purple-200" aria-live="polite">
              {formatCurrency(results.amountWithVAT, results.precision)}
            </span>
          </div>
          <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
            {isAddCalculation ? 'Вычисленная итоговая сумма' : 'Исходная сумма'}
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Тип расчета" 
          value={isAddCalculation ? 'Начисление НДС' : 'Выделение НДС из суммы'} 
          color={isAddCalculation ? 'green' : 'blue'} 
          icon={isAddCalculation ? <PlusCircle className="w-4 h-4"/> : <MinusCircle className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Ставка НДС" 
          value={vatRateDisplay} 
          color="orange" 
          icon={<Percent className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Исходная сумма" 
          value={formatCurrency(isAddCalculation ? results.amountWithoutVAT : results.amountWithVAT, results.precision)} 
          icon={<DollarSign className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Расчетная формула" 
          value={isAddCalculation 
            ? `${formatCurrency(results.amountWithoutVAT, results.precision)} × (1 + ${results.vatRate / 100}) = ${formatCurrency(results.amountWithVAT, results.precision)}`
            : `${formatCurrency(results.amountWithVAT, results.precision)} ÷ (1 + ${results.vatRate / 100}) = ${formatCurrency(results.amountWithoutVAT, results.precision)}`
          } 
          color="indigo"
          icon={<Calculator className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Формула расчета НДС" 
          value={isAddCalculation 
            ? `${formatCurrency(results.amountWithoutVAT, results.precision)} × ${results.vatRate / 100} = ${formatCurrency(results.vatAmount, results.precision)}`
            : `${formatCurrency(results.amountWithVAT, results.precision)} - (${formatCurrency(results.amountWithVAT, results.precision)} ÷ (1 + ${results.vatRate / 100})) = ${formatCurrency(results.vatAmount, results.precision)}`
          } 
          color="green"
          icon={<Percent className="w-4 h-4"/>} 
        />

        {/* Пояснение по расчету */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <AlertCircle className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Пояснение по расчету НДС</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {isAddCalculation ? (
                  <>
                    При <strong>начислении НДС</strong> сумма налога рассчитывается от исходной суммы без НДС. 
                    Итоговая сумма с учетом НДС = исходная сумма × (1 + ставка/100).
                  </>
                ) : (
                  <>
                    При <strong>выделении НДС</strong> из общей суммы сначала определяется сумма без налога 
                    путем деления на коэффициент (1 + ставка/100), затем вычисляется сумма налога как разница 
                    между исходной суммой и суммой без НДС.
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;